<template>
	<div class="flowAnalysis">
		<client-module class="module_one" titleSize="20px">
			<div slot="moduletitle">
				自然流量分析
				<el-tooltip
					class="item_tip"
					effect="dark"
					content="该数据对接Google Search Console数据报告，衡量您网站的搜索流量和搜索排名情况，不包含付费流量。"
					placement="top"
				>
					<i class="el-icon-question blue"></i>
				</el-tooltip>

				<!-- 网址切换 -->
				<sky-dropdown-select
					class="site-list"
					@change="changeSite"
					v-model="siteId"
					:options="websiteList"
					:props="{ label: 'name', value: 'id' }"
				></sky-dropdown-select>
			</div>
			<div class="module_one_body">
				<div class="time_body">
					<span class="tiem_span">选择时间:</span>
					<ig-daterange
						@change="timeChange"
						:showTip="false"
						v-model="selectTime"
						initType="DAYS_7"
						:clearable="false"
					></ig-daterange>
				</div>

				<div class="showData_body">
					<div v-for="item in showList" class="showData_item">
						<span class="value">{{ item.value }}</span>
						<div>
							<span class="name">{{ item.name }}</span>
							<el-tooltip class="item" effect="light" :content="item.tipText" placement="top">
								<i class="el-icon-question blue"></i>
							</el-tooltip>
						</div>
					</div>
				</div>

				<div class="chart_body">
					<ve-line
						ref="veLine"
						:data="chartData"
						:settings="chartSettings"
						legend-position="bottom"
						:dataEmpty="dataEmpty"
						:lineLoading="lineLoading"
						:after-config="afterConfig"
					></ve-line>
				</div>
			</div>
		</client-module>

		<client-module class="module_two" titleSize="20px" :showTitle="false">
			<div class="list_body">
				<div class="tabsBody">
					<el-tabs v-model="activeName" @tab-click="setColumn">
						<el-tab-pane
							:label="item.label"
							:name="item.value"
							v-for="item in tabList"
							:key="item.value"
						/>
					</el-tabs>
				</div>
				<data-list
					:table-style="{ name: 'webclient' }"
					:data="data"
					:columns="column"
					:loading="loading"
				></data-list>
			</div>
		</client-module>
	</div>
</template>

<script>
import listPageCreate from '@/mixins/listPageCreate.js';
import clientModule from '@/components/client/clientModule/clientModule';
import igDaterange from '@/components/client/ig-daterange';
import api from '@/backend/apiConfig/apiConfig';
import flowAnalysisSer from '@/backend/services/flowAnalysis/flowAnalysisSer';
import skyDropdownSelect from '@/components/iglobalwin/formComponents/sky-dropdownSelect';
import { getDateRegion } from '@/common/util.js';
import { mapGetters } from 'vuex';
import { getWebsiteByCustomer } from '@/backend/services/account/service';

const list = listPageCreate({
	searchConfig: {
		api: api.flowAnalysis.analytics,
		method: 'POST',
	},
	getQuery(obj, that) {
		let { selectTime, domain, activeName } = that;
		let [startDate, endDate] = selectTime;
		return Object.assign(obj, {
			startDate,
			endDate,
			domain,
			dimensions: [activeName],
		});
	},
	afterResponse(res) {
		return {
			data: {
				data: res.data,
			},
		};
	},
});

export default {
	name: 'flowAnalysis',
	components: {
		clientModule,
		igDaterange,
		skyDropdownSelect,
	},
	mixins: [list],
	data() {
		let date = getDateRegion('DAYS_7', 'YYYY-MM-DD', '&').split('&');
		let start = date[0];
		let end = date[1];
		return {
			activeName: 'query',
			selectTime: [start, end],
			lineLoading: false,
			dataEmpty: false,
			siteId: '',
			websiteList: [],
			domain: '--',
			showList: [
				{
					name: '总曝光数',
					value: '--',
					key: 'impressions',
					tipText:
						'总曝光数是指用户在搜索结果中看到指向您网站的链接的次数。对于图片和其他搜索结果类型，该值的计算方法会有所不同，具体取决于相应结果是否被滚动到了用户的视野范围内。',
				},
				{
					name: '总点击数',
					value: '--',
					key: 'clicks',
					tipText:
						'总点击次数是指用户通过点击相应链接转到您的网站的次数，其计算方式取决于搜索结果的类型。',
				},
				{
					name: '点击率',
					value: '--',
					key: 'ctr',
					tipText: '平均点击率是指带来点击的展示所占的百分比。',
				},
				{
					name: '平均排名',
					value: '--',
					key: 'position',
					tipText:
						'平均排名是指您的网站在搜索结果中的平均排名（由系统使用每当您的网站在搜索结果中出现时的最高排名计算得出）。图表下方的表格中提供了单个网页的排名。排名确定方式可能会因轮换展示或知识面板等功能而变得比较复杂。',
				},
			],
			tabList: [
				{
					value: 'query',
					label: '热门关键词',
				},
				{
					value: 'page',
					label: '热门页面',
				},
				{
					value: 'country',
					label: '国家地区',
				},
				{
					value: 'device',
					label: '访问设备',
				},
			],
			column: [],
			pubColumn: [
				{
					label: '曝光数',
					bind: 'impressions',
					showOverflowTooltip: true,
				},
				{
					label: '点击数',
					bind: 'clicks',
					showOverflowTooltip: true,
				},
				{
					label: '点击率',
					bind: val => {
						return `${val.ctr}%`;
					},
					showOverflowTooltip: true,
				},
				{
					label: '平均排名',
					bind: 'position',
					showOverflowTooltip: true,
				},
			],
			chartSettings: {
				labelMap: {
					impressions: '曝光数',
					clicks: '点击数',
					ctr: '点击率',
					position: '平均排名',
				},
				axisSite: { right: ['ctr'] },
			},
			chartData: {
				columns: ['key', 'impressions', 'clicks', 'ctr', 'position'],
				rows: [],
			},
		};
	},
	computed: {
		...mapGetters(['customerId']),
	},
	watch: {
		customerId: {
			async handler(val) {
				if (val) this.getWebsite();
				else this.$message.warning('您需要先选择一个客户');
			},
			immediate: true,
			deep: true,
		},
	},
	methods: {
		getData() {
			this.getTotalData();
			this.getChartData();
			this.setColumn();
		},
		// 网站下拉数据
		async getWebsite() {
			try {
				let { customerId } = this;
				let result = await getWebsiteByCustomer({
					customerId,
				});
				this.websiteList = result.data;
				if (result.data.length == 0) return false;
				this.siteId = result.data[0].id;
				this.domain = result.data[0].name;
				this.getData();
			} catch (error) {
				console.log(error);
			}
		},
		setColumn() {
			let { activeName } = this;
			let obj;
			switch (activeName) {
				case 'query':
					obj = {
						label: '关键词',
						bind: 'key',
					};
					break;
				case 'page':
					obj = {
						label: '页面链接',
						type: 'percentage',
						bind: 'key',
						minWidth: 350,
						style: {
							color: '#409EFF',
							'user-select': 'none',
							cursor: 'pointer',
						},
						webskip(val) {
							window.open(val);
						},
					};
					break;
				case 'country':
					obj = {
						label: '国家/地区',
						bind: 'key',
					};
					break;
				case 'device':
					obj = {
						label: '访问设备',
						bind: 'key',
					};
					break;
				default:
					break;
			}
			this.column = [obj, ...this.pubColumn];
			this.query();
		},
		// 时间选择切换
		timeChange(value) {
			this.selectTime = value;
			this.getData();
		},
		// 网址选择切换
		changeSite(id) {
			let obj = this.websiteList.find(item => {
				return item.id === id;
			});
			this.domain = obj.name;
			this.getData();
		},
		async analytics(param) {
			let res = await flowAnalysisSer.analytics(param);
			return res;
		},
		// 获取统计数据
		async getTotalData() {
			let { selectTime, domain } = this;
			let [startDate, endDate] = selectTime;
			let res = await this.analytics({
				startDate,
				endDate,
				domain,
			});
			if (res.data && res.data.length > 0) {
				let data = res.data[0];
				this.showList = this.showList.map(item => {
					return {
						...item,
						value: item.key === 'ctr' ? `${data[item.key]}%` : data[item.key],
					};
				});
			}
		},
		// 获取图表数据
		async getChartData() {
			let { selectTime, domain } = this;
			let [startDate, endDate] = selectTime;
			this.lineLoading = true;
			let res = await this.analytics({
				dimensions: ['date'],
				startDate,
				endDate,
				domain,
			});
			if (res.data && res.data.length > 0) {
				this.chartData.rows = res.data;
				this.lineLoading = false;
				this.dataEmpty = false;
			} else {
				this.dataEmpty = true;
			}
		},
		// 图表显示数据处理
		afterConfig(options) {
			// 设置你的右侧y轴
			options.yAxis[1] = {
				axisLabel: {
					formatter: '{value}%', //设置你的右侧y轴的数值展现形式
				},
			};
			options.tooltip = {
				...options.tooltip,
				formatter: function formatter(items) {
					var tpl = [];
					var _items$ = items[0],
						name = _items$.name,
						axisValueLabel = _items$.axisValueLabel;

					var title = name || axisValueLabel;
					tpl.push(title + '<br>');
					items.forEach(function (_ref) {
						var seriesName = _ref.seriesName,
							data = _ref.data,
							marker = _ref.marker;

						var itemData = Array.isArray(data) ? data[1] : data;
						if (_ref.seriesName === '点击率') {
							itemData = `${itemData}%`;
						}
						tpl.push(marker);
						tpl.push(seriesName + ': ' + itemData);
						tpl.push('<br>');
					});
					return tpl.join('');
				},
			};
			return options;
		},
	},
};
</script>

<style lang="scss" scoped>
.flowAnalysis {
	min-height: calc(100vh - 175px);
	margin: 0px 20px 10px 20px;

	.module_one {
		margin-bottom: 20px;
		background-color: #fff;

		.site-list {
			margin-left: 20px;
		}

		.item_tip {
			font-size: 14px;
		}

		.module_one_body {
			.time_body {
				margin: 30px 30px 0;

				display: flex;
				align-items: center;
				.tiem_span {
					margin-right: 20px;
				}
			}

			.showData_body {
				width: 100%;
				display: flex;
				justify-content: center;
				margin-top: 30px;
				.showData_item {
					margin: 0 70px;
					display: flex;
					flex-direction: column;
					align-items: center;

					.value {
						font-size: 24px;
						font-weight: 500;
						color: #333333;
						margin-bottom: 10px;
					}

					.name {
						font-size: 14px;
						font-weight: 400;
						color: rgba(102, 102, 102, 0.75);
						margin-right: 5px;
					}
				}
			}
		}
	}

	.module_two {
		background-color: #fff;

		.tabsBody {
			::v-deep  .el-tabs {
				.el-tabs__header {
					// margin: 0px !important;

					// .el-tabs__item {
					// 	font-size: 16px;
					// }

					.el-tabs__item.is-top:nth-child(2) {
						padding-left: 30px;
					}
				}
			}
		}
	}
}
</style>
